import type { ImportJobView } from '@wodi/shared';

import { finishUpload, IMPORT_CHUNK_BYTES, putImportChunk } from '../api/terminology.ts';

// Sends a file to an import in 1 MiB parts (plan §4.2, step 2). Parts the server already
// has are skipped, so an upload interrupted by a lost connection or a refresh carries on
// where it stopped once the person chooses the same file again. The file itself is never
// stored in the browser.

export interface UploadProgress {
  sentBytes: number;
  totalBytes: number;
}

export function chunkCountOf(size: number): number {
  return Math.ceil(size / IMPORT_CHUNK_BYTES);
}

/** Uploads every part the server is missing, then asks it to check the file. */
export async function uploadFile(
  job: Pick<ImportJobView, 'id' | 'receivedChunks'>,
  file: Blob,
  onProgress: (progress: UploadProgress) => void,
): Promise<ImportJobView> {
  const total = file.size;
  const have = new Set(job.receivedChunks);
  const count = chunkCountOf(total);
  let sent = Math.min(total, have.size * IMPORT_CHUNK_BYTES);
  onProgress({ sentBytes: sent, totalBytes: total });
  for (let seq = 0; seq < count; seq += 1) {
    if (have.has(seq)) continue;
    const start = seq * IMPORT_CHUNK_BYTES;
    const part = file.slice(start, Math.min(total, start + IMPORT_CHUNK_BYTES));
    await putImportChunk(job.id, seq, part);
    sent = Math.min(total, sent + part.size);
    onProgress({ sentBytes: sent, totalBytes: total });
  }
  return finishUpload(job.id);
}
